/** @format */

import {
	BundlePrice,
	ComingSoonFlags,
	DefiningAttributeProperty,
	DefiningAttributeValue,
	DescriptiveAttribute,
	DescriptiveAttributesParsed,
	IATBEvent,
	IDefiningAttribute,
	IProduct,
	IProductPrice,
	ISku,
	ISkuPrice,
	LaunchConfig,
	Price,
	ProductDetails,
	ProductQV,
	ProductServiceMultiEndpointResponse,
	PromotionDetailsMap,
	RatingSummaryResponse,
	ReviewSummary,
	Shipping,
	SkuLevelAttributes,
	SkuLevelPrices,
	StoreLayerItem
} from '@shared/data-access/interfaces';
import { Injectable, TransferState, makeStateKey } from '@angular/core';
import { ProductPageTemplateType, SkuPriceCollectionName } from '@shared/data-access/enums';

import { BasicDefiningAttributes } from '@pdp/data-access/interfaces';
import { Chain } from '@dcsg-ngx-ecommerce/core/model/chain.model';
import { Observable } from 'rxjs';
import { ProductDao } from '../dao/product.dao';
import { Schemas } from './schemas.interface';
import { StoreService } from '@dcsg-ngx-ecommerce/core/service/store.service';
import { UtilityService } from '@dcsg-ngx-ecommerce/core/service/utility.service';
import { map } from 'rxjs/operators';

const priceDisplay: any = {
	minOfferPrice: '',
	maxOfferPrice: '',
	minListPrice: '',
	maxListPrice: '',
	minSavingPct: '',
	maxSavingPct: '',
	emptyPrice: false
};

@Injectable({
	providedIn: 'root'
})
export class ProductService {
	/**
	 * @description
	 * Used to keep a history of sku color
	 */
	private colorBucketMap = {};

	/**
	 * @description
	 * Used to keep a history of sku price by color attribute
	 */
	private colorPriceMap = {};

	/**
	 * @description
	 * Used to keep a history of sku price per color attribute
	 */
	private multiPriceColorMap = {};

	/**
	 * @description
	 * Flag for if all SKUs are "coming soon", defaulted to true
	 */
	private areAllSkusComingSoon = true;

	/**
	 * @description Flag for all SKUs having the same pre-sale end date
	 */
	private doAllSkusHaveSamePreSaleEndDate = true;

	/**
	 * @description Pre-sale end date for SKUs to check if they are all the same
	 */
	private skuPreSaleEndDate: string;

	/**
	 * @description Flag for all SKUs having the same LEADTIME
	 */
	private doAllSkusHaveSameLeadTime = true;

	/**
	 * @description LEADTIME for SKUs to check if they are all the same
	 */
	private skuLeadTime: number;

	readonly PRODUCT_BY_ID_STATE_KEY_PREFIX = 'product-';

	constructor(
		private productDao: ProductDao,
		private chain: Chain,
		private state: TransferState,
		private utilityService: UtilityService,
		private storeService: StoreService
	) {}

	/**
	 * @description
	 * Accepts the currently selected attributes, the total available attribute value permutations, and the most recently selected attribute name
	 * and iterates through the selected attributes and returns a filtered array of possible permutations based on the current selections
	 */
	public getAvailableDefiningAttributePermutationsForSelection(
		selectedDefiningAttributes: any,
		attributeValuePermutations: any,
		currentDefiningAttributeName: string
	) {
		// Filtered attribute permutations for comparison
		let filteredAttributeValuePermutations = attributeValuePermutations;

		// Available attribute permutations, Default available permutations for current defining attribute to all
		let availableAttributeValuePermutations = attributeValuePermutations;

		// Iterate through the currently selected defining attribute values
		for (const key of Object.keys(selectedDefiningAttributes) as any) {
			// Check to see if the current defining attribute name is NOT the current selected attribute
			if (currentDefiningAttributeName !== key) {
				filteredAttributeValuePermutations = this.getPossibleDefiningAttributePermutations(
					key,
					selectedDefiningAttributes[key].value,
					filteredAttributeValuePermutations
				);
			}
		}

		// Update the current attributes available permutations from the selections if any filters have been applied
		if (filteredAttributeValuePermutations.length < attributeValuePermutations.length) {
			// Set the filtered permutations to be returned
			availableAttributeValuePermutations = filteredAttributeValuePermutations;
		}

		// Return the available attribute value permutations
		return availableAttributeValuePermutations;
	}

	/**
	 * @description
	 * Iterates through defining attributes for a given SKU if they are present (multi-variant SKUs only) and will return
	 * an array of the attribute values.
	 */
	public getDefiningAttributeValuesForSku(sku: ISku): DefiningAttributeValue[] {
		// Return array of attribute values
		const attributeValues: DefiningAttributeValue[] = [];

		// Ensure there are defining attributes to iterate
		if (typeof sku.defAttributes !== 'undefined') {
			// Iterate the defining attributes
			for (let index = 0, length = sku.defAttributes.length; index < length; index += 1) {
				// Add the current attribute value to the return array
				attributeValues[index] = {
					name: sku.defAttributes[index].name,
					description:
						sku.descAttributesParsed.sizeRange ??
						sku.descAttributesParsed.shopByAge?.split(' (').shift(),
					value: sku.defAttributes[index].value,
					id: `${sku.catentryId}`
				};
			}
		}

		// Return the attribute values
		return attributeValues;
	}

	/**
	 * @description
	 * Accepts an array of descriptive attributes and returns a list of displayable ones if they exist, otherwise an empty
	 * array is returned.
	 */
	public getDisplayableDescriptiveAttributes(
		descriptiveAttributes: DescriptiveAttribute[]
	): DescriptiveAttribute[] {
		// Displayable attributes
		const displayableAttributes = [];

		// Ensure there are attributes to iterate
		if (Array.isArray(descriptiveAttributes) && descriptiveAttributes.length > 0) {
			// Iterate the attributes
			for (let i = 0, length = descriptiveAttributes.length; i < length; i += 1) {
				// Check to see if the attribute is displayable
				if (this.utilityService.isTruthy(descriptiveAttributes[i].displayable)) {
					// check if dsg pro tips is in the array and pro tips flag is true
					if (
						descriptiveAttributes[i].name.toLowerCase() === 'dsg pro tips' &&
						this.chain.proTipsFlag
					) {
						// Push the attribute to the return array
						displayableAttributes.push(descriptiveAttributes[i]);
					} else if (
						descriptiveAttributes[i].name.toLocaleLowerCase() !== 'dsg pro tips'
					) {
						// Push the attribute to the return array
						displayableAttributes.push(descriptiveAttributes[i]);
					}
				}
			}
		}

		// Return the displayable attributes
		return displayableAttributes;
	}

	/**
	 * @description
	 * Accepts an array of descriptive attributes and returns a list of displayable ones if they exist, otherwise an empty
	 * array is returned.
	 */
	getDisplayableDescriptiveAttributesQV(
		descriptiveAttributes: BasicDefiningAttributes[]
	): DescriptiveAttribute[] {
		// Displayable attributes
		const displayableAttributes: DescriptiveAttribute[] = [];

		// Ensure there are attributes to iterate
		if (Array.isArray(descriptiveAttributes) && descriptiveAttributes.length > 0) {
			// Iterate the attributes
			for (let i = 0; i < descriptiveAttributes.length; i += 1) {
				// Check to see if the attribute is displayable
				if (this.utilityService.isTruthy(descriptiveAttributes[i]?.isDisplayable)) {
					// check if dsg pro tips is in the array and pro tips flag is true
					if (
						descriptiveAttributes[i].name.toLowerCase() === 'dsg pro tips' &&
						this.chain.proTipsFlag
					) {
						// Push the attribute to the return array
						displayableAttributes.push(descriptiveAttributes[i]);
					} else if (
						descriptiveAttributes[i].name.toLocaleLowerCase() !== 'dsg pro tips'
					) {
						// Push the attribute to the return array
						displayableAttributes.push(descriptiveAttributes[i]);
					}
				}
			}
		}

		// Return the displayable attributes
		return displayableAttributes;
	}

	/**
	 * @description
	 * Accepts a price object and returns productDisplay Price
	 */
	public getPrice(price: Price, isSeePriceInCart = false): IProductPrice {
		let productPrice: IProductPrice;

		// Check if current scenario is "See Price in Cart"
		if (isSeePriceInCart) {
			productPrice = {
				minOfferPrice: '',
				maxOfferPrice: '',
				minListPrice: price.minList ? price.minList : '',
				maxListPrice: price.maxList ? price.maxList : '',
				minSavingPct: '',
				maxSavingPct: '',
				emptyPrice: true
			};
		} else {
			// Check to see if there are any offer or list price ranges
			if (price.minOffer === price.maxOffer && price.minList === price.maxList) {
				productPrice = Object.assign({}, this.getSimpleProductPrice(price));
			} else {
				productPrice = Object.assign({}, this.getComplexProductPrice(price));
			}
		}

		return productPrice;
	}

	/**
	 * @description
	 * Accepts a product ID (eCode or catEntryId) and makes the call to the data access method to retrieve the Product data.
	 * Once the data has been returned from the API, it is converted into a Product instance and populated with the
	 * necessary properties
	 *
	 * NOTE: This method will first check TransferState to see if the parsed Product already exists so that it doesn't have to
	 * perform all of the looping and checks again on the client.  If it does not exist, it will call the DAO to get it and then
	 * set the Product in TransferState for any subsequent method calls.  This is typically done at the DAO layer, but because
	 * there is a lot of Array looping, we should take care to prevent unnecessary looping client-side
	 */
	public getProductByPartNumberOrCatalogEntryId$(
		productId: string,
		channel: string,
		clearanceFlag?: string,
		categoryId?: string,
		productParamType?: string,
		retries?: number,
		useNestJSProductService = false
	): Observable<IProduct> {
		return this.productDao
			.getProductByPartNumberOrCatalogEntryId$(
				productId,
				channel,
				clearanceFlag,
				categoryId,
				productParamType,
				retries,
				useNestJSProductService
			)
			.pipe(
				map((tempProduct: IProduct) => {
					// Get the Product instance
					const product: IProduct = Object.assign(tempProduct);

					this.setProductInfo(product);

					// Check to see if there are SKUs to iterate
					if (Array.isArray(product.skus) && product.skus.length > 0) {
						// Set the template type
						product.pageTemplate = ProductPageTemplateType.STANDARD;

						// Map the Product
						this.mapProduct(product);
					} else if (product.ecodes && product.ecodes.length) {
						// Set the template type
						product.pageTemplate = ProductPageTemplateType.BUNDLE;

						product.bundlePrice = this.getBundlePrice(product.price);

						const mappedBundledProducts = [];

						let combinedSkuIdList = [];

						const outOfStockProducts = [];

						// Iterate over bundle products (ecodes)
						for (const ecode of product.ecodes) {
							const bundleProduct: IProduct = Object.assign(ecode);

							this.setProductInfo(bundleProduct);

							// If our bundle product has a sku then it is available
							if (ecode.skus.length > 0) {
								// Map the Product
								this.mapProduct(bundleProduct);

								// Iterate over the bundle products defAttributes to find its default color if it exists
								let defAttrIndex = 0;

								while (
									!bundleProduct.defaultColor &&
									defAttrIndex < bundleProduct.defAttributes.length
								) {
									bundleProduct.defaultColor =
										this.getDefaultColorFromDefAttribute(
											bundleProduct.defAttributes[defAttrIndex]
										);

									++defAttrIndex;
								}

								mappedBundledProducts.push(bundleProduct);

								combinedSkuIdList = [
									...combinedSkuIdList,
									...bundleProduct.skuIdList
								];
							} else {
								outOfStockProducts.push(bundleProduct);
							}
						}

						if (product.ecodes.length - outOfStockProducts.length > 1) {
							product.ecodes = [...mappedBundledProducts, ...outOfStockProducts];
							product.skuIdList = combinedSkuIdList;
							product.isBundle = true;
						} else {
							product.available = false;
						}
					} else {
						// Set the template type
						product.pageTemplate = ProductPageTemplateType.OUT_OF_STOCK;

						// There are not any SKUs, set the Product to unavailable
						product.available = false;
					}

					// Return the parsed product
					return product;
				})
			);
	}

	/**
	 * @description
	 * Maps product sku data and attributes
	 * @param product
	 */
	public mapProduct(product: IProduct): void {
		// Initialize the SKU ID list
		product.skuIdList = [];

		// Get the number of SKUs
		const numberOfSkus = product.skus.length;

		// Check to see how many SKUs are available
		if (numberOfSkus === 1) {
			// Only a single SKU is available or in-stock, set the flag
			product.hasSingleSKU = true;
		}

		product.availableCombinations = [];
		product.colorAttributeBuckets = [];
		product.singleColorAttributeBucket = [];

		const hasColorAttribute = this.hasColorAttribute(product.defAttributes);

		const skuLevelAttributes: SkuLevelAttributes = {
			regularPriceAttributeList: null,
			clearanceAttributeList: null,
			markDownAttributeList: null,
			combinationColorAttributeList: null
		};

		const skuLevelPrices: SkuLevelPrices = {
			regPrice: null,
			clearancePrice: null,
			markdownPrice: null
		};

		this.iterateThroughSkus(
			product,
			numberOfSkus,
			hasColorAttribute,
			skuLevelAttributes,
			skuLevelPrices
		);

		// Set all SKUs coming soon flag
		product.allSkusHaveComingSoon = this.areAllSkusComingSoon;

		// Check to see if all SKUs have the same pre-sale end date
		if (this.doAllSkusHaveSamePreSaleEndDate && typeof this.skuPreSaleEndDate === 'string') {
			// Update the eCode pre-sale flag to true
			product.shippingInfo.isPreSale = true;

			// Update the eCode shippingInfo to use the SKU pre-sale end date
			product.shippingInfo.preSaleDate = this.skuPreSaleEndDate;
		}

		// Check to see if all SKUs have the same pre-sale end date
		if (this.doAllSkusHaveSameLeadTime && typeof this.skuLeadTime === 'number') {
			// Update the eCode shippingInfo to use the SKU lead time
			product.shippingInfo.leadTime = this.skuLeadTime;
		}

		// Set color buckets by sku level pricing
		this.setSingleColorAttributeBucket(skuLevelAttributes, product);

		this.setColorAttributeBuckets(skuLevelPrices, skuLevelAttributes, product);
		if (product.colorPriceMap) {
			product.isProductPricedAndEmptyPrice = this.doesProductHavePriceAndSeeInCart(
				product.colorPriceMap
			);
		}

		// Disable sku level pricing if we don't have more than one color bucket
		product.disableSkuLevelPricing =
			product.disableSkuLevelPricing || product.colorAttributeBuckets.length < 2;
	}

	public getAddOnSkuByEcode$(
		eCodeList: string,
		channel: string,
		version = 'v1'
	): Observable<ProductServiceMultiEndpointResponse> {
		return this.productDao.getAddOnSkuByEcode$(eCodeList, channel, 1, version);
	}

	public getAddOnwithNestSkuByEcode$(
		eCodeList: string,
		channel: string,
		version = 'v1',
		requestType?: string
	): Observable<ProductServiceMultiEndpointResponse> {
		return this.productDao.getAddOnSkuByEcode$(eCodeList, channel, 1, version, requestType);
	}

	public getProductByPartNumberOrCatalogEntryIdRawData$(
		productId: string,
		channel: string,
		clearanceFlag?: string,
		categoryId?: string,
		productParamType?: string,
		retries?: number,
		useNestJSProductService = false
	): Observable<IProduct> {
		return this.productDao.getProductByPartNumberOrCatalogEntryId$(
			productId,
			channel,
			clearanceFlag,
			categoryId,
			productParamType,
			retries,
			useNestJSProductService
		);
	}

	/**
	 * @description
	 * Parses attribute and pre-sale information and returns an Shipping Object based on the data
	 */
	public getShippingInfo(
		descAttributesParsed: DescriptiveAttributesParsed,
		isPreSale: boolean
	): Shipping {
		// Displayable attributes
		const shippingInfo: Shipping = {
			shippingClass: [],
			isHazmat: false,
			isRestricted: false,
			leadTime: 0,
			isPreSale: false,
			preSaleDate: undefined,
			isHotMarket: false
		};

		// Set the hazmat flag
		shippingInfo.isHazmat = this.utilityService.isTruthy(descAttributesParsed.isHazmat);

		// Set the restricted flag
		shippingInfo.isRestricted = this.utilityService.isTruthy(descAttributesParsed.isRestricted);

		// Set the "hot market" flag
		shippingInfo.isHotMarket = this.utilityService.isTruthy(
			descAttributesParsed.dsgHotMarketFlag
		);

		// Check pre-sale
		if (typeof isPreSale !== 'undefined') {
			shippingInfo.isPreSale = this.utilityService.isTruthy(isPreSale);

			shippingInfo.preSaleDate = descAttributesParsed.preSaleEndDate;
		}

		// Check leadtime
		if (typeof descAttributesParsed.leadTime !== 'undefined') {
			shippingInfo.leadTime = descAttributesParsed.leadTime;
		}

		// Check ship class
		if (
			typeof descAttributesParsed.shipClass === 'string' &&
			shippingInfo.shippingClass.indexOf(descAttributesParsed.shipClass.toLowerCase()) === -1
		) {
			shippingInfo.shippingClass.push(descAttributesParsed.shipClass.toLowerCase());
		}

		// Return the displayable attributes
		return shippingInfo;
	}

	/**
	 * @description
	 * Returns Promotion details for a given Product ID
	 */
	public getPromotionsByProductId$(
		catEntryId: string | number,
		channelId: string,
		skuIdList?: any,
		useNewPricingService?: boolean,
		retries?: number
	): Observable<PromotionDetailsMap> {
		return this.productDao.getPromotionsByProductId$(
			catEntryId,
			channelId,
			skuIdList,
			useNewPricingService,
			retries
		);
	}

	/**
	 * @description
	 * Accepts a text string (phrase) and an array of banned words and checks to see
	 * if any of the words in it are present in the array
	 */
	public isPhraseBanned(phrase: string, arrayOfBannedWords: string[]): boolean {
		// Ensure we have something to compare
		if (
			typeof phrase === 'string' &&
			phrase.trim() !== '' &&
			Array.isArray(arrayOfBannedWords) &&
			arrayOfBannedWords.length > 0
		) {
			// Phrase words
			const phraseWords = phrase.toLowerCase().trim().split(' ');

			// Iterate through the array of banned words as the outer loop so we check every possible banned word against supplied phrase
			for (
				let arrayOfBannedWordsIndex = 0,
					arrayOfBannedWordsLength = arrayOfBannedWords.length;
				arrayOfBannedWordsIndex < arrayOfBannedWordsLength;
				arrayOfBannedWordsIndex += 1
			) {
				// Iterate through the phrase words
				for (
					let phraseWordsIndex = 0, phraseWordsLength = phraseWords.length;
					phraseWordsIndex < phraseWordsLength;
					phraseWordsIndex += 1
				) {
					// Compare the current banned word against the current phrase word
					if (
						typeof arrayOfBannedWords[arrayOfBannedWordsIndex] === 'string' &&
						arrayOfBannedWords[arrayOfBannedWordsIndex].toLowerCase().trim() ===
							phraseWords[phraseWordsIndex].trim()
					) {
						// There is a matching word, don't perform any further checks and return true
						return true;
					}
				}
			}
		}

		// Return false by default
		return false;
	}

	/**
	 * @returns True or False if product is coming soon
	 *
	 * @description
	 * Checks to see if product is coming soon
	 *
	 */
	public isProductComingSoon(stringDate: string): boolean {
		const currentDate = new Date();

		const comingSoonDate = new Date(`${stringDate} GMT-0400`);
		return Boolean(comingSoonDate > currentDate);
	}

	/**
	 * @description
	 * Checks to see if product is pre-sale
	 */
	public isProductPreSale(stringDate: string): boolean {
		const currentDate = new Date();
		const preSaleEndDate = new Date(`${stringDate}`);
		return Boolean(preSaleEndDate > currentDate);
	}

	/**
	 * @description
	 * Accepts a product
	 * Sets basic info on product
	 */
	public setProductInfo(product: IProduct) {
		// Default the Product to available
		product.available = true;

		// Get the parsed descriptive attributes
		product.descAttributesParsed = product.descAttributesParsed || {};

		// Get the displayble descriptive attributes for long description
		product.displayableDescriptiveAttributes = this.getDisplayableDescriptiveAttributes(
			product.descAttributes
		);

		// Calculate the price
		product.calculatedPrice = this.getPrice(product.price, product.seePriceInCart);

		// Get the parsed long description
		product.longDescription = this.getParsedLongDescription(product.longDescription);

		// populate Shipping Info at the product level
		product.shippingInfo = this.getShippingInfo(
			product.descAttributesParsed,
			product.isEcodePresale
		);

		// Default sku level pricing
		product.disableSkuLevelPricing = false;

		// disable sku level pricing for launch products
		if (product.descAttributesParsed.isSecureLaunch) {
			product.disableSkuLevelPricing = true;
		}
	}

	/**
	 * @description
	 * Accepts a product, number of skus for a product, has a color attribute boolean, sku level attributes and sku level prices
	 * Iterates through skus and sets sku information
	 */
	private iterateThroughSkus(
		product: IProduct,
		numberOfSkus: number,
		hasColorAttribute: boolean,
		skuLevelAttributes: SkuLevelAttributes,
		skuLevelPrices: SkuLevelPrices
	) {
		const colorPriceMap = {};

		// Iterate the SKUs
		for (let i = 0; i < numberOfSkus; i += 1) {
			// Add the SKU to the list
			product.skuIdList.push(product.skus[i].id);

			product.skus[i].descAttributesParsed = product.skus[i].descAttributesParsed || {};

			// Check the SKU level "hot market" flag
			if (
				typeof product.skus[i].descAttributesParsed.dsgHotMarketFlag === 'undefined' ||
				product.skus[i].descAttributesParsed.dsgHotMarketFlag === null
			) {
				// Use the "hot market" flag from the product level
				product.skus[i].descAttributesParsed.dsgHotMarketFlag =
					product.descAttributesParsed.dsgHotMarketFlag;
			}

			// See if the product is coming soon at sku level
			const comingSoonEndDate = product.skus[i].descAttributesParsed?.comingSoonEndDate;

			// set each sku to their coming soon descAttr
			product.skus[i].skuComingSoonEndDate = comingSoonEndDate;

			// if the sku has a coming soon date and all previous skus have had coming soon date
			if (comingSoonEndDate && this.areAllSkusComingSoon) {
				// on the first time through populate earliestComingSoonDate
				if (!product.earliestComingSoonDate) {
					product.earliestComingSoonDate = comingSoonEndDate;
				}
				// if sku coming soon date is earlier that earliest save date
				if (
					this.utilityService.isFirstDateBeforeSecond(
						comingSoonEndDate,
						product.earliestComingSoonDate
					)
				) {
					// replace earliest save date
					product.earliestComingSoonDate = comingSoonEndDate;
				}
				// else sku didnt have coming soon attr so set variable to false
			} else {
				this.areAllSkusComingSoon = false;
			}

			// populate Shipping Info at the SKU level
			product.skus[i].shippingInfo = this.getShippingInfo(
				product.skus[i].descAttributesParsed,
				product.skus[i].skuPreSale
			);

			// Get the current SKU's pre-sale end date
			const currentSkuPreSaleEndDate = product.skus[i].shippingInfo.preSaleDate;

			// Check pre-sale date if all SKUs are pre-sale
			if (!product.skus[i].skuPreSale) {
				// Current SKU is NOT pre-sale, flip the flag
				this.doAllSkusHaveSamePreSaleEndDate = false;
			} else if (
				typeof this.skuPreSaleEndDate !== 'string' &&
				typeof currentSkuPreSaleEndDate === 'string' &&
				product.skus[i].skuPreSale
			) {
				// Set the end date for the first SKU that has a pre-sale end date
				this.skuPreSaleEndDate = currentSkuPreSaleEndDate;
			} else if (
				typeof this.skuPreSaleEndDate === 'string' &&
				this.skuPreSaleEndDate !== currentSkuPreSaleEndDate &&
				product.skus[i].skuPreSale
			) {
				// All SKUs do not have the same pre-sale end date, flip the flag
				this.doAllSkusHaveSamePreSaleEndDate = false;
			}

			// Get the current SKU's pre-sale end date
			const currentSkuLeadTime = product.skus[i].shippingInfo.leadTime;

			// Check lead time if all SKUs have lead time
			if (typeof this.skuLeadTime !== 'number' && typeof currentSkuLeadTime === 'number') {
				// Set the lead time for the first SKU
				this.skuLeadTime = currentSkuLeadTime;
			} else if (
				typeof this.skuLeadTime === 'number' &&
				this.skuLeadTime !== currentSkuLeadTime
			) {
				// All SKUs do not have the same lead time, flip the flag
				this.doAllSkusHaveSameLeadTime = false;
			}

			// Check the SKU level ship classes
			if (
				Array.isArray(product.skus[i].shippingInfo.shippingClass) &&
				product.skus[i].shippingInfo.shippingClass.length > 0
			) {
				// Iterate the SKU ship classes
				product.skus[i].shippingInfo.shippingClass.forEach((shipClass) => {
					// Check the product level ship classes
					if (
						Array.isArray(product.shippingInfo?.shippingClass) &&
						product.shippingInfo.shippingClass.indexOf(shipClass) === -1
					) {
						// Add the current ship class to the product level
						product.shippingInfo.shippingClass.push(shipClass);
					}
				});
			}

			const definingAttributeValues = this.getDefiningAttributeValuesForSku(product.skus[i]);
			// Check to see if there are any defining attribute valuess
			if (Object.keys(definingAttributeValues).length > 0) {
				// Add the permutation to the array
				product.availableCombinations.push(definingAttributeValues);
			}

			// Sku level pricing logic
			if (hasColorAttribute) {
				this.setSkuLevelColorAttributes(product.skus[i], product, skuLevelAttributes);
				this.splitColorsIntoBuckets(
					product.skus[i],
					product,
					skuLevelPrices,
					skuLevelAttributes
				);
				this.matchPriceToColor(product.skus[i], colorPriceMap);
			}

			product.seePriceInCart =
				(!product.seePriceInCart &&
					skuLevelPrices.clearancePrice &&
					product.skus[i].price.offerPrice < product.skus[i].price.thresholdPrice) ||
				product.seePriceInCart;
		}

		product.colorPriceMap = Object.assign({}, colorPriceMap);
	}

	/**
	 * @description Checks if the product colors have the same type of pricing attached to it (See price in cart type or price attached to it).
	 * Returns true if product has color skus that has empty price as true and also as false.
	 *
	 */
	private doesProductHavePriceAndSeeInCart(colorPriceMap) {
		const productPricesArr: any[] = Object.values(colorPriceMap);
		// Checks if all colors have the same value for empty price, if they don't then this product has 'see price in cart' and a price tag.
		const isEmptyPriceSameForAll = productPricesArr.every(
			(value: any) => value.price.emptyPrice === productPricesArr[0].price.emptyPrice
		);
		return !isEmptyPriceSameForAll;
	}

	/**
	 * @description Populates colorPriceMap with the prices for each different color sku of the current product.
	 * colorPriceMap will have keys that represent the name of the color and the values will be the corresponding prices.
	 */
	private matchPriceToColor(sku: ISku, colorPriceMap: any, isSeePriceInCart = false) {
		let skuColor;

		if (Array.isArray(sku.defAttributes)) {
			for (let index = 0; index < sku.defAttributes.length; index++) {
				const defAtt = sku.defAttributes[index];

				if (defAtt.name.toLowerCase() === 'color') {
					skuColor = defAtt.value;
				}
			}
		}

		const skuCalculatedPrice = this.getProductPriceFromSku(sku.price, isSeePriceInCart);

		if (colorPriceMap[skuColor]) {
			// 'From' case for displaying prices on color swatches
			if (
				skuCalculatedPrice.minOfferPrice &&
				colorPriceMap[skuColor].price.minOfferPrice &&
				skuCalculatedPrice.minOfferPrice !== colorPriceMap[skuColor].price.minOfferPrice
			) {
				colorPriceMap[skuColor].hasRange = true;
			}
			if (
				(skuCalculatedPrice.minOfferPrice &&
					skuCalculatedPrice.minOfferPrice <
						colorPriceMap[skuColor].price.minOfferPrice) ||
				colorPriceMap[skuColor].price.emptyPrice
			) {
				colorPriceMap[skuColor].price = skuCalculatedPrice;
			}
		} else {
			colorPriceMap[skuColor] = {};
			colorPriceMap[skuColor].price = skuCalculatedPrice;
		}
	}

	private hasColorAttribute(defAttributes: IDefiningAttribute[]): boolean {
		let defIndex = 0;
		let hasColorAttribute = false;
		while (defIndex < defAttributes.length && !hasColorAttribute) {
			if (defAttributes[defIndex].name.toLowerCase() === 'color') {
				hasColorAttribute = true;
			}
			++defIndex;
		}

		return hasColorAttribute;
	}

	/**
	 * @description
	 * Accepts a sku, product, sku level prices, sku level attributes and whethcr the current product has a color attribute
	 * Creates sku level pricing and sku level attributes depending on the sku price
	 */
	private splitColorsIntoBuckets(
		sku: ISku,
		product: IProduct,
		skuLevelPrices: SkuLevelPrices,
		skuLevelAttributes: SkuLevelAttributes
	) {
		if (!product.disableSkuLevelPricing) {
			if (sku.price.offerPrice === product.price.maxList) {
				product.disableSkuLevelPricing = this.colorInMultipleBuckets(
					sku,
					SkuPriceCollectionName.REGULAR
				);
				skuLevelAttributes.regularPriceAttributeList = this.assignAttributesToBuckets(
					sku,
					skuLevelAttributes.regularPriceAttributeList
				);
				skuLevelPrices.regPrice = this.calculateBucketPrice(skuLevelPrices.regPrice, sku);
			} else if (this.isSkuPriceClearance(sku.price)) {
				product.disableSkuLevelPricing = this.colorInMultipleBuckets(
					sku,
					SkuPriceCollectionName.CLEARANCE
				);
				skuLevelAttributes.clearanceAttributeList = this.assignAttributesToBuckets(
					sku,
					skuLevelAttributes.clearanceAttributeList
				);
				skuLevelPrices.clearancePrice = this.calculateBucketPrice(
					skuLevelPrices.clearancePrice,
					sku
				);
			} else {
				product.disableSkuLevelPricing = this.colorInMultipleBuckets(
					sku,
					SkuPriceCollectionName.MARKDOWN
				);
				skuLevelAttributes.markDownAttributeList = this.assignAttributesToBuckets(
					sku,
					skuLevelAttributes.markDownAttributeList
				);
				skuLevelPrices.markdownPrice = this.calculateBucketPrice(
					skuLevelPrices.markdownPrice,
					sku
				);
			}
		}
	}

	/**
	 * @description
	 * Accepts a sku and an attributeList
	 * Creates a attribute list
	 */
	private assignAttributesToBuckets(
		sku: ISku,
		attributeList: IDefiningAttribute
	): IDefiningAttribute {
		if (Array.isArray(sku.defAttributes)) {
			for (let index = 0, length = sku.defAttributes.length; index < length; index++) {
				const attribute = sku.defAttributes[index];

				// if there are no elements yet
				if (attribute.name.toLowerCase() === 'color') {
					if (attributeList === null || attributeList === undefined) {
						const listItem = {
							// name: attribute.name , values: list, swatches: attribute.swatchImage, selected: false, property: [{name: attribute.name, value: attribute.value, available: true}]
							name: attribute.name,
							selected: false,
							property: [
								{
									name: attribute.name,
									value: attribute.value,
									available: true,
									swatch: attribute.swatchImage,
									sequence: attribute.seq
								}
							]
						};

						attributeList = listItem;
					} else {
						if (
							attributeList.property.find((x) => {
								return x.value === attribute.value;
							}) === undefined
						) {
							const propertyArray = attributeList.property;

							propertyArray.push({
								name: attribute.name,
								value: attribute.value,
								available: true,
								swatch: attribute.swatchImage,
								sequence: attribute.seq
							});

							const listItem = {
								name: attribute.name,
								selected: false,
								property: propertyArray
							};

							attributeList = listItem;
						}
					}
				}
			}
		}

		return attributeList;
	}

	/**
	 * @description
	 * Accepts sku level prices, sku level attributes and a product
	 * Creates colorAttributeBuckets
	 */
	private setColorAttributeBuckets(
		skuLevelPrices: SkuLevelPrices,
		skuLevelAttributes: SkuLevelAttributes,
		product: IProduct
	) {
		if (skuLevelPrices.regPrice) {
			skuLevelAttributes.regularPriceAttributeList.property.sort((a, b) => {
				return this.sortBySequenceThenValue(a, b);
			});
			const productPrice = this.getPrice(skuLevelPrices.regPrice);
			product.colorAttributeBuckets.push({
				bucket: {
					defAttributes: skuLevelAttributes.regularPriceAttributeList
				},
				productPrice,
				productPriceRange: productPrice,
				name: SkuPriceCollectionName.REGULAR,
				selections: {
					selectedDefiningAttributes: [],
					resolvedSku: {
						id: null,
						price: null,
						inventory: null,
						buyable: true,
						catentryId: '',
						defAttributes: [],
						descAttributes: []
					},
					quantity: 1,
					actualQuantity: 1
				},
				muted: false
			});
		}

		if (skuLevelPrices.markdownPrice) {
			skuLevelAttributes.markDownAttributeList.property.sort((a, b) => {
				return this.sortBySequenceThenValue(a, b);
			});
			const productPrice = this.getPrice(skuLevelPrices.markdownPrice);
			product.colorAttributeBuckets.push({
				bucket: {
					defAttributes: skuLevelAttributes.markDownAttributeList
				},
				productPrice,
				productPriceRange: productPrice,
				name: SkuPriceCollectionName.MARKDOWN,
				selections: {
					selectedDefiningAttributes: [],
					resolvedSku: {
						id: null,
						price: null,
						inventory: null,
						buyable: true,
						catentryId: '',
						defAttributes: [],
						descAttributes: []
					},
					quantity: 1,
					actualQuantity: 1
				},
				muted: false
			});
		}

		if (skuLevelPrices.clearancePrice) {
			skuLevelAttributes.clearanceAttributeList.property.sort((a, b) => {
				return this.sortBySequenceThenValue(a, b);
			});
			const productPrice = this.getPrice(
				skuLevelPrices.clearancePrice,
				product.seePriceInCart
			);
			product.colorAttributeBuckets.push({
				bucket: {
					defAttributes: skuLevelAttributes.clearanceAttributeList
				},
				productPrice,
				productPriceRange: productPrice,
				name: SkuPriceCollectionName.CLEARANCE,
				selections: {
					selectedDefiningAttributes: [],
					resolvedSku: {
						id: null,
						price: null,
						inventory: null,
						buyable: true,
						catentryId: '',
						defAttributes: [],
						descAttributes: []
					},
					quantity: 1,
					actualQuantity: 1
				},
				muted: false
			});
		}
	}

	/**
	 * @description
	 * Accepts sku and color attribute bucket array name
	 * Check the global colorBucketMap object to determine if the sku color has already been seen
	 */
	private colorInMultipleBuckets(
		sku: ISku,
		skuPriceCollectionName: SkuPriceCollectionName
	): boolean {
		let skuColor;
		let colorInMultipleBuckets = false;
		// Find sku color
		if (Array.isArray(sku.defAttributes)) {
			for (let index = 0; index < sku.defAttributes.length; index++) {
				const defAtt = sku.defAttributes[index];

				if (defAtt.name.toLowerCase() === 'color') {
					skuColor = defAtt.value;
				}
			}
		}
		// If our color map has a key of the current sku color then see if is already
		if (
			this.colorBucketMap[skuColor] &&
			this.colorBucketMap[skuColor] !== skuPriceCollectionName
		) {
			colorInMultipleBuckets = true;
		} else {
			this.colorBucketMap[skuColor] = skuPriceCollectionName;
		}

		return colorInMultipleBuckets;
	}

	/* New Sku Level Pricing Logic Start */

	/**
	 * @description Sets skuLevelAttributes.combinationColorAttributeList
	 * @param sku - Current sku from product.skus array
	 * @param product - Return value of productDao.getProductByPartNumberOrCatalogEntryId$
	 * @param skuLevelAttributes - Holds the combinationColorAttributeList that we will be adding our color attributes to
	 */
	private setSkuLevelColorAttributes(
		sku: ISku,
		product: IProduct,
		skuLevelAttributes: SkuLevelAttributes
	): void {
		let includePrice: boolean;
		let skuColor: string;

		// Find sku color
		if (Array.isArray(sku.defAttributes)) {
			for (let index = 0; index < sku.defAttributes.length; index++) {
				const defAtt = sku.defAttributes[index];

				if (defAtt.name.toLowerCase() === 'color') {
					skuColor = defAtt.value;
				}
			}
		}

		// Find out if color has already been found under a differenct price
		if (!this.multiPriceColorMap[skuColor]) {
			includePrice = true;
			if (this.colorHasMultiplePrices(skuColor, sku.price.offerPrice)) {
				includePrice = false;
				this.multiPriceColorMap[skuColor] = true;
			}

			skuLevelAttributes.combinationColorAttributeList = this.assignColorAttributeToList(
				sku,
				skuLevelAttributes.combinationColorAttributeList,
				includePrice,
				product.seePriceInCart
			);
		}
	}

	/**
	 * @description Determines whether or not a sku color has more than one price
	 * @param skuColor - Sku color attribute value
	 * @param price - Sku offerPrice
	 * @returns Whether or not the passed in skuColor has more than one price
	 */
	private colorHasMultiplePrices(skuColor: string, price: number): boolean {
		let colorInMultipleBuckets = false;
		// If our color map has a key of the current sku color then see if is already
		if (this.colorPriceMap[skuColor] && this.colorPriceMap[skuColor] !== price) {
			colorInMultipleBuckets = true;
		} else {
			this.colorPriceMap[skuColor] = price;
		}

		return colorInMultipleBuckets;
	}

	public getBundlePrice(price: Price): BundlePrice {
		const fullSetPrice: IProductPrice = Object.assign({}, priceDisplay);
		const individualItemPrice: IProductPrice = Object.assign({}, priceDisplay);

		fullSetPrice.minOfferPrice = price.fullSetminOffer;
		fullSetPrice.maxOfferPrice =
			price.fullSetminOffer >= price.fullSetmaxOffer ? '' : price.fullSetmaxOffer;
		fullSetPrice.minListPrice = '';
		fullSetPrice.maxListPrice = '';
		fullSetPrice.emptyPrice = false;

		individualItemPrice.minOfferPrice = price.minOffer;
		individualItemPrice.maxOfferPrice = price.maxOffer;
		individualItemPrice.minListPrice = '';
		individualItemPrice.maxListPrice = '';
		individualItemPrice.emptyPrice = false;

		// Calculating SavingPct for FullSet
		let minDiscount = Math.floor(
			((price.fullSetminList - price.fullSetminOffer) / price.fullSetminList) * 100
		);
		let maxDiscount = Math.floor(
			((price.fullSetmaxList - price.fullSetmaxOffer) / price.fullSetmaxList) * 100
		);

		if (minDiscount >= 5 || maxDiscount >= 5) {
			if (minDiscount < maxDiscount) {
				fullSetPrice.minSavingPct = minDiscount;
				fullSetPrice.maxSavingPct = maxDiscount;
				fullSetPrice.minListPrice = price.fullSetminList;
				fullSetPrice.maxListPrice = price.fullSetmaxList;
			} else if (minDiscount > maxDiscount && maxDiscount >= 5) {
				fullSetPrice.minSavingPct = maxDiscount;
				fullSetPrice.maxSavingPct = minDiscount;
				fullSetPrice.minListPrice = price.fullSetminList;
				fullSetPrice.maxListPrice = price.fullSetmaxList;
			} else {
				fullSetPrice.minSavingPct = minDiscount;
				fullSetPrice.minListPrice = price.fullSetminList;
			}
		}

		// Calculating SavingPct for Individual Items
		minDiscount = Math.floor(((price.minList - price.minOffer) / price.minList) * 100);
		maxDiscount = Math.floor(((price.maxList - price.maxOffer) / price.maxList) * 100);

		if (minDiscount >= 5 || maxDiscount >= 5) {
			if (minDiscount < maxDiscount) {
				individualItemPrice.minSavingPct = minDiscount;
				individualItemPrice.maxSavingPct = maxDiscount;
				individualItemPrice.minListPrice = price.minList;
				individualItemPrice.maxListPrice = price.maxList;
			} else if (minDiscount > maxDiscount && maxDiscount >= 5) {
				individualItemPrice.minSavingPct = maxDiscount;
				individualItemPrice.maxSavingPct = minDiscount;
				individualItemPrice.minListPrice = price.minList;
				individualItemPrice.maxListPrice = price.maxList;
			} else {
				individualItemPrice.minSavingPct = minDiscount;
				individualItemPrice.minListPrice = price.minList;
			}
		}

		return { fullSetPrice, individualItemPrice };
	}

	/**
	 * @description Converts a sku price into a product price
	 * @param skuPrice - Sku price
	 * @returns Sku price converted to a product price
	 */
	public getProductPriceFromSku(skuPrice: ISkuPrice, isSeePriceInCart = false): IProductPrice {
		const price: Price = {
			minOffer: skuPrice.offerPrice,
			maxOffer: skuPrice.offerPrice,
			minList: skuPrice.listPrice,
			maxList: skuPrice.listPrice,
			minThreshold: skuPrice.thresholdPrice,
			maxThreshold: skuPrice.thresholdPrice
		};
		return this.getPrice(price, isSeePriceInCart);
	}

	/**
	 * @description Update attributeList with new values from our sku param
	 * @param sku - Current sku from prodct.skus array
	 * @param attributeList - Object that contains a property array of color attributes
	 * @param [includePrice=false] - Whether or not our new list item should include a price
	 * @param [seePriceInCart=false] - Whether or not our new list item should show 'See price in cart'
	 * @returns Updated attributeList
	 */
	private assignColorAttributeToList(
		sku: ISku,
		attributeList: IDefiningAttribute,
		includePrice = false,
		seePriceInCart = false
	): IDefiningAttribute {
		if (Array.isArray(sku.defAttributes)) {
			for (let index = 0, length = sku.defAttributes.length; index < length; index++) {
				const attribute = sku.defAttributes[index];

				// if there are no elements yet
				if (attribute.name.toLowerCase() === 'color') {
					if (attributeList === null || attributeList === undefined) {
						const listItem = {
							// name: attribute.name , values: list, swatches: attribute.swatchImage, selected: false, property: [{name: attribute.name, value: attribute.value, available: true}]
							name: attribute.name,
							selected: false,
							property: [
								this.generateAttributeListProperty(
									attribute.name,
									attribute.value,
									attribute.swatchImage,
									attribute.seq,
									sku.price,
									true,
									includePrice,
									seePriceInCart
								)
							]
						};

						attributeList = listItem;
					} else {
						let propertyFound = false;
						let propertyIndex = 0;
						while (propertyIndex < attributeList.property.length && !propertyFound) {
							if (attributeList.property[propertyIndex].value === attribute.value) {
								attributeList.property[propertyIndex] =
									this.generateAttributeListProperty(
										attribute.name,
										attribute.value,
										attribute.swatchImage,
										attribute.seq,
										sku.price,
										true,
										includePrice,
										seePriceInCart
									);
								propertyFound = true;
							}
							++propertyIndex;
						}
						if (!propertyFound) {
							const propertyArray = attributeList.property;

							propertyArray.push(
								this.generateAttributeListProperty(
									attribute.name,
									attribute.value,
									attribute.swatchImage,
									attribute.seq,
									sku.price,
									true,
									includePrice,
									seePriceInCart
								)
							);

							const listItem = {
								name: attribute.name,
								selected: false,
								property: propertyArray
							};

							attributeList = listItem;
						}
					}
				}
			}
		}

		return attributeList;
	}
	/**
	 * @description Creates new defining attribute property
	 * @param name - Color attribtue name
	 * @param value - Color attribute value
	 * @param swatch - Color attribute swatch
	 * @param sequence - Color attribute sequence thats helps us determine which order it should be displayed
	 * @param price - Sku price
	 * @param available - Sku availability
	 * @param includePrice - Whether or not a price should be included
	 * @param seePriceInCart - Whether our defining attribute property should show 'See price in cart'
	 * @returns New defining attribute property
	 */
	private generateAttributeListProperty(
		name: string,
		value: string,
		swatch: string,
		sequence: string,
		price: ISkuPrice,
		available: boolean,
		includePrice: boolean,
		seePriceInCart: boolean
	): DefiningAttributeProperty {
		return {
			name,
			value,
			available,
			swatch,
			sequence,
			productPrice:
				includePrice || seePriceInCart ? this.getProductPriceFromSku(price) : undefined
		};
	}

	// Dont forget to add a comment here
	private setSingleColorAttributeBucket(
		skuLevelAttributes: SkuLevelAttributes,
		product: IProduct
	): void {
		let defAttributes: IDefiningAttribute;

		if (skuLevelAttributes.combinationColorAttributeList) {
			defAttributes = skuLevelAttributes.combinationColorAttributeList;

			defAttributes.property.sort((a, b) => {
				return this.sortBySequenceThenValue(a, b);
			});

			product.singleColorAttributeBucket.push({
				bucket: {
					defAttributes
				},
				productPrice: null,
				productPriceRange: null,
				name: SkuPriceCollectionName.COMBINATION,
				selections: {
					selectedDefiningAttributes: [],
					resolvedSku: {
						id: null,
						price: null,
						inventory: null,
						buyable: true,
						catentryId: '',
						defAttributes: [],
						descAttributes: []
					},
					quantity: 1,
					actualQuantity: 1
				},
				muted: false
			});
		}
	}

	/* New Sku Level Pricing Logic End */

	/**
	 * @description
	 * Accepts two defining attribute properties
	 * Returns which defining attribute property should be placed in front of the other
	 */
	public sortBySequenceThenValue(a: DefiningAttributeProperty, b: DefiningAttributeProperty) {
		if (+a.sequence < +b.sequence) {
			return -1;
		} else if (+a.sequence > +b.sequence) {
			return 1;
		} else if (+a.sequence === +b.sequence) {
			if (a.value.toString() > b.value.toString()) {
				return 1;
			} else if (a.value.toString() < b.value.toString()) {
				return -1;
			} else {
				return 0;
			}
		}
	}

	/**
	 * @description
	 * Accepts a price object and returns a parsed object.  This is for complex scenarios where there is a range price
	 */
	private getComplexProductPrice(price) {
		// Range Price -- No Savings -- Offer and List prices are equal
		if (
			price.minOffer === price.minList &&
			price.maxOffer === price.maxList &&
			price.minOffer >= price.minThreshold &&
			price.maxOffer >= price.maxThreshold
		) {
			priceDisplay.minOfferPrice = price.minOffer;
			priceDisplay.maxOfferPrice = price.maxOffer;
			priceDisplay.minListPrice = '';
			priceDisplay.maxListPrice = '';
			priceDisplay.minSavingPct = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = false;

			// Range Price -- With savings (if savings >= 5)  -- Offer is less than list
		} else if (
			price.minOffer < price.minList &&
			price.maxOffer <= price.maxList &&
			price.minOffer >= price.minThreshold &&
			price.maxOffer >= price.maxThreshold
		) {
			// Calculating SavingPct
			const minDiscount = Math.floor(
				((price.minList - price.minOffer) / price.minList) * 100
			);
			const maxDiscount = Math.floor(
				((price.maxList - price.maxOffer) / price.maxList) * 100
			);

			if (minDiscount >= 5 || maxDiscount >= 5) {
				if (minDiscount < maxDiscount) {
					priceDisplay.minSavingPct = minDiscount;
					priceDisplay.maxSavingPct = maxDiscount;
				} else if (minDiscount > maxDiscount && maxDiscount >= 5) {
					priceDisplay.minSavingPct = maxDiscount;
					priceDisplay.maxSavingPct = minDiscount;
				} else {
					priceDisplay.minSavingPct = minDiscount;
				}
			}

			priceDisplay.minOfferPrice = price.minOffer;
			priceDisplay.maxOfferPrice = price.maxOffer;
			priceDisplay.minListPrice = price.minList;
			priceDisplay.maxListPrice = price.maxList;
			priceDisplay.emptyPrice = false;

			// TODO: code refactoring
			// Range Price -- No Savings --- Various Condition
		} else if (
			price.minOffer <= price.maxOffer &&
			price.minList <= price.maxList &&
			(price.minOffer > price.minList ||
				price.maxOffer > price.maxList ||
				price.minOffer === price.minList ||
				price.maxOffer === price.maxList) &&
			price.minOffer >= price.minThreshold &&
			price.maxOffer >= price.maxThreshold
		) {
			priceDisplay.minOfferPrice = price.minOffer;
			priceDisplay.maxOfferPrice = price.maxOffer;
			priceDisplay.minListPrice = '';
			priceDisplay.maxListPrice = '';
			priceDisplay.minSavingPct = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = false;

			// Error Case + MAP Violation
		} else if (
			(!price.minOffer && !price.maxOffer) ||
			price.minOffer < price.minThreshold ||
			price.maxOffer < price.maxThreshold
		) {
			priceDisplay.minOfferPrice = '';
			priceDisplay.maxOfferPrice = '';
			priceDisplay.minListPrice = price.minList;
			priceDisplay.maxListPrice = price.maxList;
			priceDisplay.minSavingPct = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = true;
		}

		return priceDisplay;
	}

	/**
	 * @description
	 * Accepts a Price and Sku and tranforms the data into a ProductPrice interface
	 */
	private calculateBucketPrice(price: Price, sku: ISku): Price {
		const minList =
			price && price.minList < sku.price.listPrice ? price.minList : sku.price.listPrice;
		const maxList =
			price && price.maxList > sku.price.listPrice ? price.minList : sku.price.listPrice;
		const minOffer =
			price && price.minOffer < sku.price.offerPrice ? price.minOffer : sku.price.offerPrice;
		const maxOffer =
			price && price.maxOffer > sku.price.offerPrice ? price.maxOffer : sku.price.offerPrice;
		const minThresholdPrice =
			price && price.minThreshold < sku.price.thresholdPrice
				? price.minThreshold
				: sku.price.thresholdPrice;
		const maxThresholdPrice =
			price && price.maxThreshold > sku.price.thresholdPrice
				? price.maxThreshold
				: sku.price.thresholdPrice;

		return {
			maxList,
			maxOffer,
			maxThreshold: maxThresholdPrice,
			minList,
			minOffer,
			minThreshold: minThresholdPrice
		};
	}

	public getDefaultColorFromDefAttribute(defAttribute: IDefiningAttribute): string {
		if (defAttribute.name.trim().toLowerCase() === 'color') {
			if (defAttribute.swatches.indexOf(',') > -1) {
				// Get the first color
				return defAttribute.swatches
					.substring(0, defAttribute.swatches.indexOf(','))
					.replace('_swatch', '');
			} else {
				// Set the only available color
				return defAttribute.swatches.replace('_swatch', '');
			}
		}
	}

	/**
	 * @description
	 * Accepts a selected attribute name, selected attribute value, and attribute value permutations and iterates through
	 * the attribute value permutations and finds possible permutations based on matching name and value
	 */
	private getPossibleDefiningAttributePermutations(
		selectedAttributeName,
		selectedAttributeValue,
		attributeValuePermutations
	) {
		const possibleAttributeValues = [];

		if (
			typeof selectedAttributeValue === 'string' &&
			selectedAttributeValue.trim() !== '' &&
			attributeValuePermutations.length > 0
		) {
			// Iterate through the permutations
			for (
				let attrValueIndex = 0,
					attrValuePermutationsLength = attributeValuePermutations.length;
				attrValueIndex < attrValuePermutationsLength;
				attrValueIndex++
			) {
				const permutation = attributeValuePermutations[attrValueIndex];
				// Ensure there are permutations to iterate
				if (Array.isArray(permutation) && permutation.length > 0) {
					for (
						let permutationIndex = 0, permutationLength = permutation.length;
						permutationIndex < permutationLength;
						permutationIndex++
					) {
						const attributeValue = permutation[permutationIndex];
						// Check the values to see if there is a match
						if (
							attributeValue.value.toLowerCase().trim() ===
								selectedAttributeValue.trim().toLowerCase().trim() &&
							attributeValue.name.toLowerCase().trim() ===
								selectedAttributeName.trim().toLowerCase()
						) {
							// Add the matched permutation to the return array
							possibleAttributeValues.push(permutation);
						}
					}
				}
			}
		}

		return possibleAttributeValues;
	}

	/**
	 * @description
	 * Accepts a longDescription and parses it and returns a new string
	 */
	private getParsedLongDescription(longDescription: string): string | undefined {
		// Ensure there is a string to parse
		if (typeof longDescription === 'string' && longDescription.trim() !== '') {
			// Create a new string
			let parsedLongDescription = `${longDescription}`;

			/*
			 * Scene7 domain / protocol replacement
			 */
			parsedLongDescription = parsedLongDescription
				.replace('https://s7d2.scene7.com', 'https://dks.scene7.com')
				.replace('http://s7d2.scene7.com', 'https://dks.scene7.com');

			// Return the parsed long description
			return parsedLongDescription;
		}

		// Return undefined by default
		return undefined;
	}

	/**
	 * @description
	 * Gets review data from BazaarVoice for a given product by ID
	 */
	public getProductReviewSummary$(
		productId: string,
		retries?: number
	): Observable<ReviewSummary> {
		return this.productDao.getReviewsByProductId$(productId, retries).pipe(
			map((response: RatingSummaryResponse) => {
				const data = response.Includes.Products
					? response.Includes.Products[productId] ||
					  response.Includes.Products[productId.toLowerCase()] ||
					  response.Includes.Products[productId.toUpperCase()]
					: null;

				const returnData: ReviewSummary = {
					reviewCount: null,
					overallRatingRange: null,
					averageRating: null,
					recommendedCount: null,
					secondaryRatings: [],
					ratingDistribution: []
				};

				if (data) {
					returnData.reviewCount = data.ReviewStatistics.TotalReviewCount;
					returnData.averageRating = data.ReviewStatistics.AverageOverallRating;
					returnData.overallRatingRange = data.ReviewStatistics.OverallRatingRange;
					returnData.ratingDistribution = data.ReviewStatistics.RatingDistribution;
					returnData.recommendedCount = data.ReviewStatistics.RecommendedCount;
					returnData.ratingDistribution.sort((a, b) => {
						return b.RatingValue - a.RatingValue;
					});

					const keys = data.ReviewStatistics.SecondaryRatingsAveragesOrder;

					for (let i = 0; i < keys.length; ++i) {
						const key = keys[i];

						returnData.secondaryRatings.push({
							label: data.ReviewStatistics.SecondaryRatingsAverages[key].Label,
							averageRating:
								data.ReviewStatistics.SecondaryRatingsAverages[key].AverageRating,
							minLabel:
								data.ReviewStatistics.SecondaryRatingsAverages[key].MinLabel ||
								'Poor',
							maxLabel:
								data.ReviewStatistics.SecondaryRatingsAverages[key].MaxLabel ||
								'Excellent',
							valueRange:
								data.ReviewStatistics.SecondaryRatingsAverages[key].ValueRange,
							type: data.ReviewStatistics.SecondaryRatingsAverages[key].DisplayType,
							valueLabel:
								data.ReviewStatistics.SecondaryRatingsAverages[key].ValueLabel
						});
					}
				}
				return returnData;
			})
		);
	}

	public setProductInStockReminder(
		userEmail: string,
		skuId: string,
		productTitle: string,
		productURL: string,
		imageURL: string,
		attributes: { label: string; value: string }[],
		storeNumber: string,
		storeName?: string,
		retries = 0
	): Observable<any> {
		return this.productDao.setProductInStockReminder$(
			userEmail,
			skuId,
			productTitle,
			productURL,
			imageURL,
			attributes,
			storeNumber,
			storeName,
			retries
		);
	}

	/**
	 * @description
	 * Accepts a price object and returns a parsed object.  This is for simple scenarios where there is not a range price
	 */
	private getSimpleProductPrice(price) {
		// If Offer = List -- Single SKU
		if (price.minOffer === price.minList && price.minOffer >= price.minThreshold) {
			priceDisplay.minOfferPrice = price.minOffer;
			priceDisplay.minListPrice = '';
			priceDisplay.maxOfferPrice = '';
			priceDisplay.maxListPrice = '';
			priceDisplay.minSavingPct = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = false;
			// If Offer < List -- Single SKU
		} else if (price.minOffer < price.minList && price.minOffer >= price.minThreshold) {
			priceDisplay.minOfferPrice = price.minOffer;
			priceDisplay.minListPrice = price.minList;

			// Calculating SavingPct
			const discount = Math.floor(((price.minList - price.minOffer) / price.minList) * 100);
			if (discount >= 5) {
				priceDisplay.minSavingPct = discount;
			}

			priceDisplay.maxOfferPrice = '';
			priceDisplay.maxListPrice = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = false;
			// If Offer > List and is above MAP
		} else if (price.minOffer > price.minList) {
			priceDisplay.minOfferPrice = price.minOffer;
			priceDisplay.minListPrice = '';
			priceDisplay.maxOfferPrice = '';
			priceDisplay.maxListPrice = '';
			priceDisplay.minSavingPct = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = false;
			// MAP Violation or missing prices
		} else if (
			(!price.minOffer && !price.maxOffer) ||
			price.minOffer < price.minThreshold ||
			price.maxOffer < price.maxThreshold
		) {
			priceDisplay.minOfferPrice = '';
			priceDisplay.maxOfferPrice = '';
			priceDisplay.minListPrice = price.minList;
			priceDisplay.maxListPrice = price.maxList;
			priceDisplay.minSavingPct = '';
			priceDisplay.maxSavingPct = '';
			priceDisplay.emptyPrice = true;
			// todo if offer price is 0 then disable cart
			// Generic Else
		} else {
			priceDisplay.minOfferPrice = price.maxOffer;
		}

		return priceDisplay;
	}

	public isSkuPriceClearance(skuPrice: ISkuPrice): boolean {
		return (
			skuPrice.offerPrice.toFixed(2).toString().includes('.97') ||
			skuPrice.offerPrice < skuPrice.thresholdPrice
		);
	}

	/**
	 * Determines if the PLA should be displayed
	 * An object with schemas will be returned if needed
	 * @param referrer
	 * @param route
	 */
	public setupPla(referrer: string, route: any) {
		let isPLAEnabled = false;
		let campID = '';
		let emailPLA: boolean;

		// Check to see if coming from Google PLA
		if (typeof referrer === 'string' && referrer.indexOf('google.com') > -1) {
			isPLAEnabled = true;
		}

		// set the flag isPLAEnabled to true if the current URL has camp=CSE:DSG query param.
		if (typeof route?.queryParams?._value !== 'undefined') {
			// Get the camp query parameter
			campID = route.queryParams._value.camp;

			if (
				campID &&
				(campID.indexOf('CSE') > -1 ||
					campID.indexOf('SEM') > -1 ||
					campID.indexOf('SNS') > -1)
			) {
				isPLAEnabled = true;
			}

			// Get the EmailPLA query parameter
			emailPLA = this.utilityService.isTruthy(route.queryParams._value.EmailPLA);

			if (emailPLA) {
				isPLAEnabled = true;
			}
		}

		// If PLA is enabled display landing schemes else display the original schemes.
		if (isPLAEnabled) {
			return {
				scheme1: 'productlanding_rr',
				scheme2: 'product1landing_rr',
				scheme3: 'product2landing_rr'
			} as Schemas;
		}
		return undefined;
	}

	/**
	 * Returns a launch config to help determine if the product is a regular launc (ex: jordan shoe)
	 * or a coveted secure launch (ex: Yeezy)
	 * launchConfigOverride will contain configurations needed or overrides if required
	 * @param isLaunch
	 * @param isSecureLaunch
	 * @param launchConfigOverride
	 */
	public fetchLaunchConfig(isLaunch: any, isSecureLaunch: any, launchConfigOverride: any) {
		return {
			isLaunch: this.utilityService.isTruthy(isLaunch),
			isSecureLaunch: this.utilityService.isTruthy(isSecureLaunch),
			launchCookies: launchConfigOverride?.launchCookies,
			cartHeader: launchConfigOverride?.cartHeader,
			handshakeParam: launchConfigOverride?.handshakeParam,
			allowQueryParamOverride: launchConfigOverride?.allowQueryParamOverride,
			redirectURL: launchConfigOverride?.redirectURL
		} as LaunchConfig;
	}

	/**
	 * @description
	 * Will return defaultColor if product is defined
	 * @param product
	 */
	public determineProductDefaultColor(product: IProduct) {
		if (typeof product !== 'undefined' && product !== null) {
			// Default the selected color attribute
			return product.defaultColor;
		}
		return undefined;
	}

	public fetchLaunchConfigOverrides$(): Observable<any> {
		return this.productDao.fetchLaunchConfigOverride$();
	}

	/**
	 * @description
	 * Determines if store item list needs to be populated
	 * @param product
	 * @param selectedSkuId
	 * @param storeLayerItems
	 */
	public generateSkuStoreLayerList(
		product: IProduct,
		selectedSkuId: string | any,
		storeLayerItems: any
	) {
		const ecodeSearch = selectedSkuId === undefined;

		if (ecodeSearch) {
			return this.storeService.populateStoreLayerItems(
				product.skus,
				selectedSkuId,
				product.isAtLeastOneSkuBopis
			);
		} else {
			const foundSkuList = this.storeService.findSelectedSku(storeLayerItems);

			const sku = foundSkuList.length !== 1 ? undefined : foundSkuList[0].id;
			if (sku !== selectedSkuId) {
				return this.storeService.populateStoreLayerItems(
					product.skus,
					selectedSkuId,
					product.isAtLeastOneSkuBopis
				);
			}
		}
	}

	public generateBundleStoreLayerList(
		event: IATBEvent,
		isAtLeastOneSkuBopis: boolean
	): StoreLayerItem {
		const currentSku = event.skuData;
		return {
			bopisable: currentSku.descAttributesParsed.isBopisEnabled,
			isaFlag: currentSku.descAttributesParsed.isIsaEnabled,
			buyable: currentSku.buyable,
			displayHomr: currentSku.descAttributesParsed.isPersonalizable || isAtLeastOneSkuBopis,
			id: currentSku.id,
			selectedSku: true
		} as StoreLayerItem;
	}

	/**
	 * Determines ComingSoonFlags interface
	 * @param product
	 */
	public getComingSoonFlags(product: IProduct): ComingSoonFlags {
		const comingSoonFlags: ComingSoonFlags = {
			productComingSoonBool: false,
			eCodeComingSoonBool: false
		};

		if (this.isProductComingSoon(product.descAttributesParsed.comingSoonEndDate)) {
			comingSoonFlags.productComingSoonBool = true;
			comingSoonFlags.eCodeComingSoonBool = true;
			// if all skus have a coming soon date, compare the earliest coming soon date to now
		} else if (
			product.allSkusHaveComingSoon &&
			this.isProductComingSoon(product.earliestComingSoonDate)
		) {
			comingSoonFlags.productComingSoonBool = true;
		} else {
			comingSoonFlags.productComingSoonBool = false;
		}
		return comingSoonFlags;
	}

	/**
	 * @description
	 * Gets the Product details from the Product Quick View API
	 */
	public getProductByEcode$(
		productId: string,
		channel: string,
		clearanceFlag?: string,
		categoryId?: string,
		productParamType?: string,
		retries?: number
	): Observable<ProductQV> {
		// Get unique key
		const STATE_KEY = makeStateKey(
			`product-ecode-${this.PRODUCT_BY_ID_STATE_KEY_PREFIX}${productId}-${categoryId || ''}-${
				clearanceFlag || ''
			}`
		);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);
		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		return this.productDao
			.getProductByEcode$(
				productId,
				channel,
				clearanceFlag,
				categoryId,
				productParamType,
				retries
			)
			.pipe(
				map((tempProduct: any) => {
					const product: any = Object.assign(tempProduct);
					// Set the state
					this.state.set<any>(STATE_KEY, product);
					return product;
				})
			);
	}

	public getProductDetailsByEcodesV4$(
		products: any[],
		storeID: string
	): Observable<ProductDetails> {
		const ecodes = products.map((product) => product.ecode);
		return this.productDao.getProductDetailsByEcodesV4$(ecodes, storeID);
	}

	/** Quick View Logic */
	public getDefaultColorFromDefiningAttribute(defAttribute: IDefiningAttribute): string {
		if (defAttribute.name.trim().toLowerCase() === 'color') {
			if (defAttribute.value.indexOf('|') > -1) {
				// Get the first color
				return defAttribute.value.substring(0, defAttribute.value.indexOf('|'));
			} else {
				// Set the only available color
				return defAttribute.value;
			}
		}
	}

	/**
	 * @description
	 * Gets the Page object by an url and type.  Types: `category`, `product`, `name`.  The returned Page object will
	 * contain all product data related information. both ECODE and SKU level
	 */
	public getProductByUrl$(
		url: string,
		partNumber: string,
		channel: string,
		clearanceFlag?: string,
		categoryId?: string,
		productParamType?: string,
		retries?: number
	): Observable<ProductQV> {
		const STATE_KEY = makeStateKey(
			`product-url-${this.PRODUCT_BY_ID_STATE_KEY_PREFIX}${url}-${categoryId || ''}-${
				clearanceFlag || ''
			}`
		);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);
		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		return this.productDao
			.getProductByUrl$(
				url,
				partNumber,
				channel,
				clearanceFlag,
				categoryId,
				productParamType,
				retries
			)
			.pipe(
				map((tempProduct: any) => {
					const product: any = Object.assign(tempProduct);
					// Set the state
					this.state.set<any>(STATE_KEY, product);
					return product;
				})
			);
	}
	public getProductByEcodeV4$(productId: string, checkClearance = false, retries = 0) {
		return this.productDao.getProductByEcodeV4$(productId, checkClearance, retries).pipe(
			map((tempProductResponse) => {
				return tempProductResponse?.productsData[0];
			})
		);
	}

	public static generateImageUrlFromAttributes(productDetails: any) {
		const base = `https://dks.scene7.com/is/image/dkscdn/${productDetails.style.partNumber}`;

		const attributes = productDetails.style.definingAttributes;

		const imageParams = '?$UTPMain$';

		const suffix = `is${imageParams}`;

		for (let attributeIndex = 0; attributeIndex < attributes.length; attributeIndex++) {
			const currentAttribute = attributes[attributeIndex];

			if (currentAttribute.name.toLowerCase() === 'color') {
				const colorArray = currentAttribute.value.split('|');
				let sanitizedColor = colorArray[0].replace(/\//g, '_');
				sanitizedColor = sanitizedColor.replace(/ /g, '_');
				return `${base}_${sanitizedColor}_${suffix}`;
			}
		}

		return `${base}${imageParams}`;
	}

	public static generateDisplayPrice(price: any) {
		return {
			isSeePriceInCart: price.minofferprice < price.thresholdPrice,
			isRangePrice: price.minofferprice !== price.maxofferprice,
			lowestPrice: price.minofferprice,
			highestPrice: price.maxofferprice
		};
	}

	/**
	 * @description
	 * Returns Sku EDD
	 */
	public getSkuDeliveryDate$(skuId: string): Observable<any> {
		return this.productDao.getSkuDeliveryDate$(skuId);
	}
}
